import {
    type ChangeEventHandler,
    type KeyboardEventHandler,
    useEffect,
    useMemo,
    useState,
} from 'react';
import '../../../css/apiTool.css';
import '../../../css/notification.css';
import { FLOW_EDITING_TOKEN } from '../constants/graph';
import { fetchApiEndpoints, sendRequest } from '../sources/apiTool';
import translations from '../translations';
import { HttpMethod } from '../types/api';
import { stringReplace } from '../utils/string';
import ButtonDanger from './buttons/ButtonDanger';
import ButtonPrimary from './buttons/ButtonPrimary';
import CodeEditor from './generic/CodeEditor';
import Loader from './loader/Loader';
import { useNotificationDispatch } from './notifications/NotificationProvider';

type ApiToolProps = { isActive: boolean };

const ApiTool = ({ isActive }: ApiToolProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [contentType, setContentType] = useState<'json' | 'html' | 'text'>('json');
    const [requestBody, setRequestBody] = useState('');
    const [responseBody, setResponseBody] = useState('');
    const [requestUrl, setRequestUrl] = useState('');
    const [isLoadingEndpoints, setIsLoadingEndpoints] = useState(false);
    const [endpoints, setEndpoints] = useState<Record<string, string>>({});
    const [selectedFlowId, setSelectedFlowId] = useState('');
    const dispatchNotification = useNotificationDispatch();

    useEffect(() => {
        if (!isActive) {
            return;
        }

        const storedId = window.sessionStorage.getItem('selected-flow');

        setSelectedFlowId(storedId ?? '');
    }, [isActive]);

    useEffect(() => {
        const fetchEndpoints = async () => {
            try {
                setIsLoadingEndpoints(true);
                const endpoints = await fetchApiEndpoints();
                setEndpoints(endpoints);
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
            } finally {
                setIsLoadingEndpoints(false);
            }
        };

        fetchEndpoints();
    }, [dispatchNotification]);

    const handleRequest = async (method: HttpMethod) => {
        if (requestUrl.trim() === '') {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error: translations.API_TOOL_no_url_specified_error_message,
                },
            });
            return;
        }

        try {
            setIsLoading(true);

            const responseData = await sendRequest(
                requestUrl,
                method,
                method === 'GET' ? null : requestBody,
            );

            const stringifiedResponse = (() => {
                const result = JSON.stringify(responseData, null, 4);
                // Guard against values that get returned unchanged
                return typeof result !== 'string' ? '' : result;
            })();

            // If we have an Object, it's probably JSON...
            if (responseData !== null && typeof responseData === 'object') {
                setResponseBody(stringifiedResponse);
                setContentType('json');
            } else if (typeof responseData === 'string') {
                /**
                 * If the response is not JSON then it could either be
                 * a string describing a Flow package or the HTML of
                 * a player
                 */
                try {
                    /**
                     * String can be parsed as JSON, so stringify it again
                     * to unescape it (kind of obfuscates the package data)
                     */
                    JSON.parse(responseData);
                    setResponseBody(stringifiedResponse);
                    setContentType('json');
                } catch {
                    // String cannot be parsed as JSON, so we assume it is just HTML
                    setResponseBody(responseData);
                    setContentType('html');
                }
            } else {
                setResponseBody(stringifiedResponse);
                setContentType('text');
            }

            dispatchNotification({
                type: 'notifySuccess',
                payload: {
                    message: stringReplace(translations.API_TOOL_request_success_message, {
                        method,
                        requestUrl,
                    }),
                },
            });
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error,
                },
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = ({ key }) => {
        if (key !== 'Enter') {
            return;
        }

        handleRequest(HttpMethod.Get);
    };

    const handleRequestBodyChange = (value: string) => {
        setRequestBody(value);
    };

    const handleRequestUrlChange: ChangeEventHandler<HTMLInputElement> = ({
        target: { value },
    }) => {
        setRequestUrl(value);
    };

    const urls = useMemo(() => {
        return Object.keys(endpoints).map((endpoint) => {
            const parsedEndpoint = endpoint.replace('{editingToken}', FLOW_EDITING_TOKEN);

            if (!selectedFlowId) {
                return parsedEndpoint;
            }

            return parsedEndpoint
                .replace('{flow}', selectedFlowId)
                .replace('/flow/{id}', `/flow/${selectedFlowId}`);
        });
    }, [endpoints, selectedFlowId]);

    return (
        <div className="api-tool">
            <div className="api-endpoint">
                <input
                    data-testid="urlInput"
                    list="endpoints"
                    placeholder={
                        isLoadingEndpoints
                            ? translations.API_TOOL_url_input_loading_placeholder
                            : translations.API_TOOL_url_input_placeholder
                    }
                    className="form-control"
                    onChange={handleRequestUrlChange}
                    aria-label={translations.API_TOOL_url_input_label}
                    onKeyDown={handleKeyDown}
                />
                <datalist id="endpoints" data-testid="endpoints">
                    {urls.map((url) => (
                        <option key={url} value={url} label={url}>
                            {url}
                        </option>
                    ))}
                </datalist>
                <div className="margin-left nowrap">
                    <ButtonPrimary
                        className="request-button"
                        onClick={() => handleRequest(HttpMethod.Get)}
                    >
                        {translations.API_TOOL_get_request_button_label}
                    </ButtonPrimary>
                    <ButtonPrimary
                        className="request-button"
                        onClick={() => handleRequest(HttpMethod.Post)}
                    >
                        {translations.API_TOOL_post_request_button_label}
                    </ButtonPrimary>
                    <ButtonPrimary
                        className="request-button"
                        onClick={() => handleRequest(HttpMethod.Put)}
                    >
                        {translations.API_TOOL_put_request_button_label}
                    </ButtonPrimary>
                    <ButtonDanger
                        className="request-button"
                        onClick={() => handleRequest(HttpMethod.Delete)}
                    >
                        {translations.API_TOOL_delete_request_button_label}
                    </ButtonDanger>
                </div>
            </div>
            <div className="request-response-container">
                <div className="request">
                    <h4 className="editor-heading text-center">
                        {translations.API_TOOL_request_body_input_label}
                    </h4>
                    <CodeEditor
                        value={requestBody}
                        highlightActiveLine
                        name="request-editor"
                        onChange={handleRequestBodyChange}
                    />
                </div>
                <div className="response">
                    <h4 className="editor-heading text-center">
                        {translations.API_TOOL_response_body_input_label}
                    </h4>
                    <CodeEditor
                        mode={contentType}
                        value={responseBody}
                        highlightActiveLine
                        readOnly
                        name="response-editor"
                    />
                </div>
            </div>
            {isLoading && <Loader />}
        </div>
    );
};

export default ApiTool;
